import '.h{display:grid;}.ssr-plugin'
import '.kn{row-gap:1rem;}.ssr-plugin'
import '.kr{column-gap:2rem;}.ssr-plugin'
import '.ks{grid-auto-flow:column;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import { styled } from 'inlines'
import { Card } from '../../card/Card'
import { Image } from '@app/components/core/image/Image'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import { TextLink } from '@app/components/core/link/TextLink'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Row from '@app/components/layout/Row'
import { match } from 'ts-pattern'
import useMediaQuery from '@app/util/useMediaQuery'
import { AppearItem, useAppearStyle } from '../blocks/AppearInViewport'

export type CtaContentSectionType = {
  type: 'ctaGrid'
  title: string
  titleLevel?: 'H1' | 'H2' | 'H3' | 'H4'
  listItems: CtaBlockType[]
}

export type CtaBlockType = {
  type: 'cta'
  label: string
  content: string
  image: {
    src: string
    alt: string
  }
  linkText: string
  linkUrl: string
  backgroundColor: string
  size: 'large' | 'medium' | 'small'
}

type CtaBlockTypeInternal = CtaBlockType & {
  renderSectionTitle?: boolean
  staggerIndex: number
}

export const CtaContentSection = ({
  title,
  titleLevel = 'H4',
  listItems,
}: CtaContentSectionType) => {
  const isMobile = useMediaQuery()
  const rowRepeat = isMobile
    ? listItems.length
    : Math.ceil(listItems.length / 2)
  return (
    <Column gap={0} alignItems="stretch">
      {title && (
        <AppearItem staggerIndex={0}>
          <Title.Hx title={titleLevel}>{title}</Title.Hx>
        </AppearItem>
      )}
      <div className="h kn kr ks"
        style={{
          gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
          gridTemplateRows: `repeat(${rowRepeat}, auto)`,}}
      >
        {listItems.map((item, index) => (
          <CtaBlock
            key={index}
            {...item}
            staggerIndex={index + 1}
            renderSectionTitle={!title}
          />
        ))}
      </div>
    </Column>
  )
}

const CtaBlock = (props: CtaBlockTypeInternal) => {
  return match(props)
    .with({ size: 'large' }, (p) => (
      <LargeCtaBlock {...p} renderSectionTitle={p.renderSectionTitle} />
    ))
    .with({ size: 'medium' }, () => <MediumCtaBlock {...props} />)
    .with({ size: 'small' }, () => <SmallCtaBlock {...props} />)
    .exhaustive()
}

const LargeCtaBlock = ({
  label,
  content,
  image,
  linkText,
  linkUrl,
  backgroundColor,
  renderSectionTitle,
  staggerIndex,
}: CtaBlockTypeInternal) => {
  const appearStyle = useAppearStyle(staggerIndex)
  const hasImage = Boolean(image?.src)
  const hasTextLink = Boolean(linkText) && Boolean(linkUrl)
  return (
    <Card
      style={{
        ...appearStyle,
        backgroundColor: backgroundColor,
        border: `1px solid ${backgroundColor}`,
        padding: '4rem',
        borderRadius: '2.5rem',
        height: '35rem',
        gridColumn: 'span 2',
      }}
    >
      <Row
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4rem',
        }}
      >
        <Column gap={'0.5rem'}>
          {label && (
            <Title.Hx
              title={renderSectionTitle ? 'H2' : 'H3'}
              style={{
                marginBottom: 0,
              }}
            >
              {label}
            </Title.Hx>
          )}
          {content && (
            <Text.Medium
              style={{
                fontSize: '1.5rem',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                marginBottom: 0,
              }}
            >
              {content}
            </Text.Medium>
          )}
          {hasTextLink && (
            <TextLink
              to={linkUrl}
              icon={faArrowRight}
              containerStyle={{ marginTop: '1rem' }}
            >
              {linkText}
            </TextLink>
          )}
        </Column>
        {hasImage && (
          <Image
            src={image.src}
            alt={image.alt}
            style={{
              width: '50%',
            }}
          />
        )}
      </Row>
    </Card>
  )
}

const MediumCtaBlock = ({
  label,
  content,
  image,
  linkText,
  linkUrl,
  backgroundColor,
  staggerIndex,
}: CtaBlockTypeInternal) => {
  const appearStyle = useAppearStyle(staggerIndex)
  const hasImage = Boolean(image?.src)
  const hasTextLink = Boolean(linkText) && Boolean(linkUrl)
  return (
    <Card
      style={{
        ...appearStyle,
        backgroundColor: backgroundColor,
        border: `1px solid ${backgroundColor}`,
        padding: '1rem',
        borderRadius: '1.25rem',
        height: '31.5rem',
      }}
    >
      <Column
        style={{
          alignItems: 'start',
        }}
      >
        {hasImage && (
          <Image
            src={image.src}
            alt={image.alt}
            style={{
              maxHeight: '13rem',
              maxWidth: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
        <Column gap={0}>
          {label && (
            <Title.H3
              style={{
                fontSize: '2.625rem',
                marginBottom: '0.5rem',
                display: '-webkit-box',
                WebkitLineClamp: hasImage ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {label}
            </Title.H3>
          )}
          {content && (
            <Text.Medium
              style={{
                fontSize: '1.375rem',
                marginBottom: 0,
                display: '-webkit-box',
                WebkitLineClamp: hasImage ? 2 : 8,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {content}
            </Text.Medium>
          )}
        </Column>
        {hasTextLink && (
          <TextLink to={linkUrl} icon={faArrowRight}>
            {linkText}
          </TextLink>
        )}
      </Column>
    </Card>
  )
}

const SmallCtaBlock = ({
  label,
  content,
  linkUrl,
  backgroundColor,
  staggerIndex,
}: CtaBlockTypeInternal) => {
  const appearStyle = useAppearStyle(staggerIndex)
  const hasIconLink = Boolean(linkUrl)
  return (
    <Card
      to={linkUrl}
      style={{
        ...appearStyle,
        backgroundColor: backgroundColor,
        border: `1px solid ${backgroundColor}`,
        padding: '1rem',
        borderRadius: '1.25rem',
        height: '11rem',
      }}
      contentStyle={{
        justifyContent: 'center',
      }}
    >
      <Row
        style={{
          alignItems: 'center',
        }}
      >
        <Column gap={0} style={{ flex: 1 }}>
          {label && (
            <Title.H4
              style={{
                fontSize: '1.75rem',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                paddingBottom: '0.12em',
                marginBottom: '-0.12em',
              }}
            >
              {label}
            </Title.H4>
          )}
          {content && (
            <Text.Medium
              style={{
                fontSize: '1.125rem',
                marginBottom: 0,
                width: '100%',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {content}
            </Text.Medium>
          )}
        </Column>
        {hasIconLink && (
          <FontAwesomeIcon
            icon={faArrowRight}
            style={{ width: '3.5rem', height: '3.5rem' }}
          />
        )}
      </Row>
    </Card>
  )
}
